import React, { useContext, useState, useEffect, useRef } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import StarekContext from "../context/StarekContext";
import SearchResults from "../components/modals/SearchResults";
import SupportModal from "../components/modals/SupportModal";
import { gql, useQuery, useMutation } from "@apollo/client";
import moment from "moment-timezone";
import {
    PHONE_COUNTRY_CODES,
    isValidNationalPhone,
    splitPhoneNumber,
} from "../utils";

function Header(props) {
    const { hasLeftMargin = true } = props;
    const GET_USER_ACTIVITIES_QUERY = gql`
        query User($where: UserWhereUniqueInput!) {
            user(where: $where) {
                id
                name
                email
                phone
                home_timezone
                show_time_in_home_timezone
                username
                verified
                memberships {
                    role {
                        name
                    }
                }
            }
        }
    `;
    const UPDATE_USER_PROFILE = gql`
        mutation UpdateUser(
            $where: UserWhereUniqueInput!
            $data: UserUpdateInput!
        ) {
            updateOneUser(where: $where, data: $data) {
                id
                phone
                home_timezone
                show_time_in_home_timezone
            }
        }
    `;
    const starekContext = useContext(StarekContext);
    const location = useLocation();

    const { data: userData, loading: userLoading } = useQuery(
        GET_USER_ACTIVITIES_QUERY,
        {
            variables: {
                where: { id: parseInt(starekContext?.user?.id) },
            },
        },
    );

    const [updateUserProfile, { loading: updatingProfile }] =
        useMutation(UPDATE_USER_PROFILE);

    const [selected, setSelected] = useState(false);
    const [item, setItem] = useState({});
    const [phoneError, setPhoneError] = useState("");
    // Country code and the 10-digit number are edited apart, saved joined.
    const [phoneParts, setPhoneParts] = useState(() => splitPhoneNumber(null));
    const [justUpdated, setJustUpdated] = useState(false);
    const [timeZones, setTimeZones] = useState([]);
    const [showModal, setShowModal] = useState(false);
    const [showSupportModal, setShowSupportModal] = useState(
        location.hash === "#support",
    );
    const [searchText, setSearchText] = useState("");
    const [activities, setActivities] = useState([]);
    const [totalActivity, setTotalActivity] = useState(0);
    const [types, setTypes] = useState([]);
    const [noMore, setNoMore] = useState(true);
    const [page, setPage] = useState(2);
    const profilePanelRef = useRef(null);
    const profileButtonRef = useRef(null);
    const updatedTimerRef = useRef(null);

    // Clear the "Updated" timer on unmount so it can't fire after the panel is gone.
    useEffect(() => {
        return () => {
            if (updatedTimerRef.current) {
                clearTimeout(updatedTimerRef.current);
            }
        };
    }, []);

    const flashUpdated = () => {
        if (updatedTimerRef.current) clearTimeout(updatedTimerRef.current);
        setJustUpdated(true);
        updatedTimerRef.current = setTimeout(() => {
            setJustUpdated(false);
            updatedTimerRef.current = null;
        }, 3000);
    };

    // Any further edit makes the "Updated" confirmation stale — drop it immediately.
    const clearUpdated = () => {
        if (updatedTimerRef.current) {
            clearTimeout(updatedTimerRef.current);
            updatedTimerRef.current = null;
        }
        setJustUpdated(false);
    };

    useEffect(() => {
        if (!userData?.user) return;
        setItem(userData.user);
        setPhoneParts(splitPhoneNumber(userData.user.phone));
        setPhoneError("");
    }, [userData?.user]);

    useEffect(() => {
        setTimeZones(moment.tz.names());
    }, []);

    useEffect(() => {
        if (!selected) return;
        const handleOutsideClick = (event) => {
            if (
                profilePanelRef.current?.contains(event.target) ||
                profileButtonRef.current?.contains(event.target)
            ) {
                return;
            }
            setSelected(false);
        };
        document.addEventListener("mousedown", handleOutsideClick);
        return () => {
            document.removeEventListener("mousedown", handleOutsideClick);
        };
    }, [selected]);

    const toggleSearchModal = (value) => setShowModal(value);

    useEffect(() => {
        setShowSupportModal(location.hash === "#support");
    }, [location.hash]);

    const openSupportModal = () => {
        navigator(`${location.pathname}${location.search}#support`);
    };

    const closeSupportModal = () => {
        navigator(`${location.pathname}${location.search}`, {
            replace: true,
        });
    };

    const handleSwitchWorkspace = () => {
        sessionStorage.setItem(
            "postAuthRedirect",
            window.location.pathname + window.location.search,
        );
        navigator("/choose-workspace");
    };

    const navigator = useNavigate();
    return (
        <>
            <nav
                className={`main-header navbar navbar-expand navbar-white navbar-light starek-header-nav ${
                    hasLeftMargin ? "starek-header-with-margin" : ""
                }`}
            >
                {/* Workspace & Plant Section - Flattened */}
                <div className="starek-header-workspace">
                    {/* Workspace Logo & Name */}
                    <div
                        className="starek-header-workspace-link"
                        onClick={handleSwitchWorkspace}
                    >
                        {starekContext?.lastUsedWorkspace?.logo && (
                            <div className="starek-header-workspace-logo">
                                <img
                                    src={starekContext?.lastUsedWorkspace?.logo}
                                    alt="Workspace Logo"
                                    className="starek-header-workspace-logo-img"
                                />
                            </div>
                        )}
                        <span className="starek-header-workspace-name">
                            {starekContext?.lastUsedWorkspace?.name}
                        </span>
                    </div>
                </div>

                <ul className="navbar-nav ml-auto">
                    {/* User Profile & Logout Section */}
                    <li className="nav-item starek-header-user-nav-item">
                        <div className="starek-header-user-wrap">
                            {/* User Profile Button */}
                            <div
                                className="starek-header-user-button"
                                onClick={() => {
                                    setSelected((prev) => !prev);
                                }}
                                ref={profileButtonRef}
                            >
                                <div className="starek-header-avatar">
                                    <i className="fa fa-user starek-header-avatar-icon"></i>
                                    {totalActivity > 0 && (
                                        <span className="starek-header-activity-badge">
                                            {totalActivity}
                                        </span>
                                    )}
                                </div>
                                <div className="starek-header-user-meta">
                                    <span className="starek-header-user-name">
                                        {starekContext.user?.name || "User"}
                                    </span>
                                </div>
                            </div>

                            {selected && (
                                <div
                                    ref={profilePanelRef}
                                    role="dialog"
                                    aria-label="User profile"
                                    className="starek-profile-panel"
                                >
                                    <div className="starek-profile-panel-header">
                                        <div className="starek-profile-panel-title">
                                            <span className="starek-profile-panel-name">
                                                {starekContext.user?.name}
                                            </span>
                                            <span className="starek-profile-panel-email">
                                                {starekContext.user?.email ||
                                                    ""}
                                            </span>
                                        </div>
                                        <button
                                            type="button"
                                            onClick={() => setSelected(false)}
                                            className="starek-profile-panel-close"
                                            aria-label="Close profile panel"
                                        >
                                            <i className="fa fa-times"></i>
                                        </button>
                                    </div>
                                    <div className="form-group">
                                        <label htmlFor="phone">
                                            Phone Number
                                        </label>
                                        <div className="input-group">
                                            <select
                                                className="form-select flex-grow-0 w-auto"
                                                aria-label="Country code"
                                                value={phoneParts.countryCode}
                                                onChange={(e) => {
                                                    setPhoneError("");
                                                    clearUpdated();
                                                    setPhoneParts({
                                                        ...phoneParts,
                                                        countryCode:
                                                            e.target.value,
                                                    });
                                                }}
                                            >
                                                {/* Keep a saved code that isn't in the short list selectable. */}
                                                {(PHONE_COUNTRY_CODES.includes(
                                                    phoneParts.countryCode,
                                                )
                                                    ? PHONE_COUNTRY_CODES
                                                    : [
                                                          phoneParts.countryCode,
                                                          ...PHONE_COUNTRY_CODES,
                                                      ]
                                                ).map((code) => (
                                                    <option
                                                        key={code}
                                                        value={code}
                                                    >
                                                        {code}
                                                    </option>
                                                ))}
                                            </select>
                                            <input
                                                type="tel"
                                                inputMode="numeric"
                                                maxLength={10}
                                                className={`form-control ${
                                                    phoneError
                                                        ? "is-invalid"
                                                        : ""
                                                }`}
                                                id="phone"
                                                name="phone"
                                                autoComplete="tel-national"
                                                placeholder="10-digit number"
                                                value={phoneParts.number}
                                                onChange={(e) => {
                                                    setPhoneError("");
                                                    clearUpdated();
                                                    setPhoneParts({
                                                        ...phoneParts,
                                                        number: e.target.value
                                                            .replace(/\D/g, "")
                                                            .slice(0, 10),
                                                    });
                                                }}
                                            />
                                        </div>
                                        {phoneError ? (
                                            <div className="invalid-feedback d-block">
                                                {phoneError}
                                            </div>
                                        ) : (
                                            <small>
                                                <i>
                                                    Required to receive SMS
                                                    notifications. Enter the
                                                    10-digit number without
                                                    the country code.
                                                </i>
                                            </small>
                                        )}
                                    </div>
                                    <div className="form-group">
                                        <label>Home Time Zone</label>
                                        <select
                                            className="form-control"
                                            id="homeTimeZoneId"
                                            name="homeTimeZoneId"
                                            value={item?.home_timezone || ""}
                                            onChange={(e) => {
                                                clearUpdated();
                                                setItem({
                                                    ...item,
                                                    home_timezone:
                                                        e.target.value,
                                                });
                                            }}
                                        >
                                            <option value="0">
                                                --Select--
                                            </option>
                                            {timeZones.map((s, i) => (
                                                <option key={i} value={s}>
                                                    {s}
                                                </option>
                                            ))}
                                        </select>
                                    </div>
                                    <div className="form-group">
                                        <label>Always Use Home Timezone</label>
                                        <input
                                            type="checkbox"
                                            className="ml-3"
                                            id="show_time_in_home_timezone"
                                            name="show_time_in_home_timezone"
                                            checked={
                                                !!item?.show_time_in_home_timezone
                                            }
                                            onChange={() => {
                                                clearUpdated();
                                                setItem({
                                                    ...item,
                                                    show_time_in_home_timezone:
                                                        !item?.show_time_in_home_timezone,
                                                });
                                            }}
                                        ></input>
                                        <br />
                                        <small>
                                            <i>
                                                Enabling this will{" "}
                                                <strong>not</strong> show times
                                                in your current timezone
                                            </i>
                                        </small>
                                    </div>
                                    <div className="starek-profile-panel-actions">
                                        <button
                                            className={`btn btn-sm ${
                                                justUpdated
                                                    ? "btn-success"
                                                    : "btn-info btn-primary"
                                            }`}
                                            disabled={
                                                userLoading ||
                                                updatingProfile ||
                                                !item?.id
                                            }
                                            onClick={async () => {
                                                if (!item?.id) return;
                                                const homeTimezoneValue =
                                                    item?.home_timezone || null;
                                                // An empty number clears the phone.
                                                const phoneNumber =
                                                    phoneParts.number;
                                                // A legacy saved number that doesn't fit the
                                                // 10-digit rule must not block unrelated saves
                                                // (e.g. timezone) while it is left untouched.
                                                const phoneUnchanged =
                                                    `${phoneParts.countryCode}${phoneNumber}` ===
                                                    (item?.phone || "");
                                                if (
                                                    phoneNumber &&
                                                    !phoneUnchanged &&
                                                    !isValidNationalPhone(
                                                        phoneNumber,
                                                    )
                                                ) {
                                                    setPhoneError(
                                                        "Phone number must be exactly 10 digits.",
                                                    );
                                                    return;
                                                }
                                                const phoneValue = phoneNumber
                                                    ? `${phoneParts.countryCode}${phoneNumber}`
                                                    : null;
                                                setPhoneError("");
                                                try {
                                                    const result =
                                                        await updateUserProfile(
                                                            {
                                                                variables: {
                                                                    where: {
                                                                        id: parseInt(
                                                                            item.id,
                                                                        ),
                                                                    },
                                                                    data: {
                                                                        phone: {
                                                                            set: phoneValue,
                                                                        },
                                                                        home_timezone:
                                                                            {
                                                                                set: homeTimezoneValue,
                                                                            },
                                                                        show_time_in_home_timezone:
                                                                            {
                                                                                set: !!item?.show_time_in_home_timezone,
                                                                            },
                                                                    },
                                                                },
                                                            },
                                                        );
                                                    if (
                                                        result?.data
                                                            ?.updateOneUser
                                                    ) {
                                                        setItem(
                                                            result.data
                                                                .updateOneUser,
                                                        );
                                                        setPhoneParts(
                                                            splitPhoneNumber(
                                                                result.data
                                                                    .updateOneUser
                                                                    .phone,
                                                            ),
                                                        );
                                                        flashUpdated();
                                                        starekContext.showSuccess(
                                                            "Profile updated successfully.",
                                                        );
                                                    }
                                                } catch (error) {
                                                    const message =
                                                        error?.message ||
                                                        "Failed to update profile.";
                                                    if (
                                                        /phone/i.test(message)
                                                    ) {
                                                        setPhoneError(message);
                                                    } else {
                                                        starekContext.showError(
                                                            message,
                                                        );
                                                    }
                                                }
                                            }}
                                        >
                                            {updatingProfile
                                                ? "Saving..."
                                                : justUpdated
                                                  ? "Updated"
                                                  : "Update"}
                                        </button>
                                        <button
                                            className="btn btn-sm btn-info"
                                            onClick={() => {
                                                setSelected(false);
                                            }}
                                        >
                                            Close
                                        </button>
                                    </div>
                                </div>
                            )}

                            {/* Support Link */}
                            <span
                                className="nav-link starek-header-support"
                                role="button"
                                title="Support"
                                onClick={openSupportModal}
                            >
                                <div className="starek-header-support-icon">
                                    <i className="fa fa-headset starek-header-logout-icon-inner"></i>
                                </div>
                            </span>

                            {/* Logout Button */}
                            <span
                                className="nav-link starek-header-logout"
                                role="button"
                                title="Logout"
                                onClick={async () => {
                                    await starekContext.logout();
                                }}
                            >
                                <div className="starek-header-logout-icon">
                                    <i className="fa fa-sign-out-alt starek-header-logout-icon-inner"></i>
                                </div>
                            </span>
                        </div>
                    </li>
                </ul>
            </nav>
            {showModal && (
                <SearchResults
                    visibility={showModal}
                    initialSearchText={searchText}
                    setVisibility={toggleSearchModal}
                    setInitialSearchText={setSearchText}
                />
            )}
            <SupportModal
                open={showSupportModal}
                onClose={closeSupportModal}
            />
        </>
    );
}

export default Header;
